// Native-storage bridge.
//
// When the app runs inside the Electron or Tauri wrapper we mirror every
// localStorage write to an on-disk JSON file, and we hydrate localStorage from
// that file on boot. React hooks stay synchronous and keep talking to
// localStorage directly, but the source of truth on desktop becomes the disk
// file. In the browser we're a no-op passthrough.

const ELECTRON_BRIDGE_KEY = "gitlabDashNative";
const DISK_MIGRATED_FLAG = "gitlab-dash:disk-storage-migrated";
const DISK_PRIORITY_FLAG = "gitlab-dash:disk-storage-priority";

const detectBackend = () => {
	if (typeof window === "undefined") return null;
	const electron = window[ELECTRON_BRIDGE_KEY];
	if (electron?.storage && electron.platform === "electron") {
		return { kind: "electron", api: electron.storage };
	}
	if (window.__TAURI_INTERNALS__ || window.__TAURI__) {
		return { kind: "tauri", api: null };
	}
	return null;
};

const backend = detectBackend();

// Skip caches, session-only data, auth state, and other high-churn keys.
// Anything auth- or cache-related must NEVER be mirrored to disk because a
// stale disk value hydrated on relaunch can break OIDC nonces/state.
const isPersistableKey = (key) => {
	if (typeof key !== "string") return false;
	if (key.startsWith("gitlab-dash:")) return false;
	if (key.startsWith("gitlab-mr-data")) return false;
	if (key.startsWith("gitlab-diff-cache")) return false;
	if (key.startsWith("gitlab-mr-notes-cache")) return false;
	if (key.startsWith("repos-page-merge-request-cache")) return false;
	if (key.startsWith("recent-pushes-without-mr-cache")) return false;
	if (key.startsWith("gitlab-dash-post-login-redirect")) return false;
	if (key === "gitlab_user_data") return false;
	// oidc-client-ts writes keys like "oidc.user:...", "oidc.abc123" (state),
	// and internal nonce/code_verifier entries. Any of these on disk will break
	// login on relaunch because the state is single-use.
	if (key.startsWith("oidc.")) return false;
	if (key.startsWith("oidc:")) return false;
	return true;
};

const invokeTauri = async (command, args) => {
	if (typeof window === "undefined") return undefined;
	const invoke = window.__TAURI_INTERNALS__?.invoke || window.__TAURI__?.invoke;
	if (!invoke) throw new Error("Tauri invoke bridge missing");
	return invoke(command, args);
};

const diskApi = {
	getAll: async () => {
		if (!backend) return {};
		if (backend.kind === "electron") {
			return (await backend.api.getAll()) || {};
		}
		return (await invokeTauri("storage_get_all")) || {};
	},
	set: async (key, value) => {
		if (!backend) return false;
		if (backend.kind === "electron") return backend.api.set(key, value);
		return invokeTauri("storage_set", { key, value });
	},
	remove: async (key) => {
		if (!backend) return false;
		if (backend.kind === "electron") return backend.api.remove(key);
		return invokeTauri("storage_remove", { key });
	},
	clear: async () => {
		if (!backend) return false;
		if (backend.kind === "electron") return backend.api.clear();
		return invokeTauri("storage_clear");
	},
	writeAll: async (payload) => {
		if (!backend) return false;
		if (backend.kind === "electron") return backend.api.writeAll(payload);
		return invokeTauri("storage_write_all", { payload });
	},
	getPath: async () => {
		if (!backend) return null;
		if (backend.kind === "electron") return backend.api.getPath();
		return invokeTauri("storage_get_path");
	},
	reveal: async () => {
		if (!backend) return false;
		if (backend.kind === "electron") return backend.api.reveal();
		return false;
	},
};

let hydrationPromise = null;
let installed = false;
let writeQueue = Promise.resolve();

const scheduleWrite = (task) => {
	writeQueue = writeQueue.catch(() => {}).then(task);
	return writeQueue;
};

// Wrap localStorage.setItem/removeItem so every write mirrors to disk while
// keeping the API synchronous for hooks.
const installLocalStorageProxy = () => {
	if (installed || typeof window === "undefined") return;
	installed = true;

	const originalSetItem = Storage.prototype.setItem;
	const originalRemoveItem = Storage.prototype.removeItem;
	const originalClear = Storage.prototype.clear;

	Storage.prototype.setItem = function patchedSetItem(key, value) {
		originalSetItem.call(this, key, value);
		if (this === window.localStorage && isPersistableKey(key)) {
			let parsed;
			try {
				parsed = JSON.parse(value);
			} catch {
				parsed = value;
			}
			scheduleWrite(() =>
				diskApi.set(key, parsed).catch((error) => {
					console.error("nativeStorage: failed to persist key", key, error);
				}),
			);
		}
	};

	Storage.prototype.removeItem = function patchedRemoveItem(key) {
		originalRemoveItem.call(this, key);
		if (this === window.localStorage && isPersistableKey(key)) {
			scheduleWrite(() =>
				diskApi.remove(key).catch((error) => {
					console.error("nativeStorage: failed to remove key", key, error);
				}),
			);
		}
	};

	Storage.prototype.clear = function patchedClear() {
		originalClear.call(this);
		if (this === window.localStorage) {
			scheduleWrite(() =>
				diskApi.clear().catch((error) => {
					console.error("nativeStorage: failed to clear disk store", error);
				}),
			);
		}
	};
};

const hydrateFromDisk = async () => {
	if (!backend) return { hydrated: 0 };
	try {
		const data = await diskApi.getAll();
		let hydrated = 0;
		// Purge any keys that should never have been on disk (older builds may
		// have written OIDC state before we excluded it). Do this in one batch.
		const toPurge = Object.keys(data || {}).filter((k) => !isPersistableKey(k));
		if (toPurge.length > 0) {
			const cleaned = { ...data };
			for (const k of toPurge) delete cleaned[k];
			scheduleWrite(() => diskApi.writeAll(cleaned).catch(() => {}));
		}
		for (const [key, value] of Object.entries(data || {})) {
			if (!isPersistableKey(key)) continue;
			try {
				// Everything stored via useLocalStorage went through JSON.stringify
				// on write, so always JSON.stringify on hydration too. If disk
				// somehow holds a raw string (legacy or hand-edited), we still want
				// to write a valid JSON scalar into localStorage so the hooks'
				// JSON.parse doesn't blow up.
				const serialized = JSON.stringify(value);
				const current = window.localStorage.getItem(key);
				if (current !== serialized) {
					// The patched setItem echoes back to disk but that write is
					// idempotent (same key/value) so we just accept the round-trip.
					window.localStorage.setItem(key, serialized);
				}
				hydrated += 1;
			} catch (error) {
				console.warn("nativeStorage: failed to hydrate", key, error);
			}
		}
		return { hydrated };
	} catch (error) {
		console.error("nativeStorage: hydration failed", error);
		return { hydrated: 0, error };
	}
};

export const isDesktop = () => Boolean(backend);
export const desktopBackend = () => backend?.kind || null;

export const ensureHydrated = () => {
	if (!backend) return Promise.resolve({ hydrated: 0 });
	if (!hydrationPromise) {
		installLocalStorageProxy();
		hydrationPromise = hydrateFromDisk();
	}
	return hydrationPromise;
};

// Copy every eligible localStorage key to disk in one shot.
export const migrateLocalStorageToDisk = async () => {
	if (!backend) return { migrated: 0, skipped: 0 };
	const payload = {};
	let skipped = 0;
	for (let i = 0; i < window.localStorage.length; i += 1) {
		const key = window.localStorage.key(i);
		if (!key) continue;
		if (!isPersistableKey(key)) {
			skipped += 1;
			continue;
		}
		const raw = window.localStorage.getItem(key);
		if (raw === null) continue;
		try {
			payload[key] = JSON.parse(raw);
		} catch {
			payload[key] = raw;
		}
	}
	// Merge with existing disk keys rather than overwriting, so we don't lose
	// values that only live on disk from a previous session.
	const existing = await diskApi.getAll();
	const merged = { ...existing, ...payload };
	await diskApi.writeAll(merged);
	window.localStorage.setItem(DISK_MIGRATED_FLAG, String(Date.now()));
	window.localStorage.setItem(DISK_PRIORITY_FLAG, "true");
	return { migrated: Object.keys(payload).length, skipped };
};

export const exportSettings = async () => {
	if (backend) {
		return diskApi.getAll();
	}
	const out = {};
	for (let i = 0; i < window.localStorage.length; i += 1) {
		const key = window.localStorage.key(i);
		if (!key || !isPersistableKey(key)) continue;
		const raw = window.localStorage.getItem(key);
		if (raw === null) continue;
		try {
			out[key] = JSON.parse(raw);
		} catch {
			out[key] = raw;
		}
	}
	return out;
};

export const importSettings = async (payload) => {
	if (!payload || typeof payload !== "object") {
		throw new Error("Invalid settings payload");
	}
	let imported = 0;
	for (const [key, value] of Object.entries(payload)) {
		if (!isPersistableKey(key)) continue;
		const serialized =
			typeof value === "string" ? value : JSON.stringify(value);
		window.localStorage.setItem(key, serialized);
		imported += 1;
	}
	return { imported };
};

export const getStoragePath = async () => {
	if (!backend) return null;
	return diskApi.getPath();
};

export const revealStorageFile = async () => {
	if (!backend) return false;
	return diskApi.reveal();
};

export const hasMigrated = () =>
	window.localStorage.getItem(DISK_MIGRATED_FLAG) !== null;

export const getMigrationTimestamp = () => {
	const raw = window.localStorage.getItem(DISK_MIGRATED_FLAG);
	if (!raw) return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
};
